import { useSelector } from 'react-redux';
import c from 'classnames';

import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import { getHomePageTinyAccountId } from 'store/home';

import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  LinkButton,
  TextLink,
} from 'components';
import { Typography, TextColor, Icon } from '@repo/ui';
import { HomeNextStepProfile } from './steps/HomeNextStepProfile';

import styles from './HomeNextSteps.module.scss';
import getRenderedComponentProps from '@repo/utils/getRenderedComponentProps';
import { CheckIcon } from 'views/Information/NextStep';

type NextStepProps = {
  title: string;
  isActive: boolean;
  Status: React.ReactNode;
  clickable?: {
    text: string;
    url: string;
    isExternal?: boolean;
    onClick?: () => Promise<void>;
  };
  inlineLinkPostCompletion?: {
    text: string;
    url: string;
    isExternal?: boolean;
    onClick?: () => Promise<void>;
  };
  consultLink?: {
    prefixText?: string;
    text: string;
    url: string;
    isExternal?: boolean;
    onClick?: () => Promise<void>;
  };
  description?: string;
  isBlocking?: boolean;
  descriptionBlocking?: string;
  isCompleted?: boolean;
  CompletedContent?: React.ReactNode;
};
export const HomeNextStep = ({
  title,
  isActive,
  Status,
  clickable,
  inlineLinkPostCompletion,
  consultLink,
  description,
  isCompleted,
  CompletedContent,
  isBlocking,
  descriptionBlocking,
}: NextStepProps) => {
  const statusProps = getRenderedComponentProps<{ text: string }>(Status);

  return (
    <div className='flex align-items-start gap-2'>
      {!statusProps.text ? Status : <CheckIcon isCompleted={false} />}
      <div className={c(styles.titleRow, isActive && styles.active)}>
        {isCompleted && !!inlineLinkPostCompletion ? (
          <TextLink
            label={title}
            to={inlineLinkPostCompletion.url}
            external={!!inlineLinkPostCompletion.isExternal}
            color={TextColor.GREY}
            onClick={inlineLinkPostCompletion.onClick}
            className={styles.titleLink}
            size={'heading-m'}
            iconNameRight={'chevronForward'}
            iconSize='m'
          />
        ) : (
          <div className={styles.title}>
            <Typography variant='heading-m'>{title}</Typography>
            {statusProps.text && Status}
          </div>
        )}
        {isCompleted && !!CompletedContent && CompletedContent}
        {!!consultLink && (
          <div className={styles.forceLineHeight}>
            {consultLink.prefixText && (
              <Typography variant='label' className='mr-1'>
                {consultLink.prefixText}
              </Typography>
            )}
            <TextLink
              label={consultLink.text}
              to={consultLink.url}
              external={!!consultLink.isExternal}
              size={'label'}
              color={TextColor.PURPLE}
              onClick={consultLink.onClick}
              className={styles.underline}
            />
          </div>
        )}
        {!!description && (
          <div className={styles.forceLineHeight}>
            <Typography variant='label'>{description}</Typography>
          </div>
        )}
        {isBlocking && !!descriptionBlocking && (
          <div className={styles.forceLineHeight}>
            <Typography variant='label'>{descriptionBlocking}</Typography>
          </div>
        )}

        {!!clickable && isActive && (
          <LinkButton
            label={clickable.text}
            href={clickable.url}
            state={{ from: '/' }}
            external={!!clickable.isExternal}
            variant={ButtonVariants.SOLID}
            size={ButtonSizes.M}
            color={ButtonColors.PURPLE}
            onClick={clickable.onClick}
            width={'content'}
          />
        )}
      </div>
    </div>
  );
};

export function HomeNextSteps() {
  const tinyAccountId = useSelector(getHomePageTinyAccountId);
  const { latestKit, stepsList } = useTinyAccountForHome(tinyAccountId);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className='hide-on-mobile'>
          <Typography variant='heading-l'>{`Next Steps`}</Typography>
        </div>
        <div className='hide-on-desktop'>
          <Typography variant='heading-xl'>{`Next Steps`}</Typography>
        </div>
        {!!latestKit?.id && (
          <Typography
            variant='label'
            color='purple'
          >{`Kit ID: ${latestKit.id}`}</Typography>
        )}
      </div>
      <HomeNextStepProfile />
      {stepsList.map((step, index) => (
        <HomeNextStep {...step} key={`${step}-${index}`} />
      ))}
    </div>
  );
}

export const HomeResultsStatus = ({ text }: { text: string }) => {
  return (
    <div
      className={c(
        styles.status,
        text === 'Ready' ? styles.green : styles.blue,
      )}
    >
      <div className={styles.dot}></div>
      <Typography variant='label'>{text}</Typography>
    </div>
  );
};
